import { useRef, useState, useEffect } from 'react';
import PauseIcon from '@/shared/assets/images/pause.svg';
import PlayIcon from '@/shared/assets/images/play.svg';
import OptimizedImage from '@/shared/ui/OptimizedImage';
import VolumeHighIcon from '@/shared/assets/images/volume-high.svg';
import VolumeOffIcon from '@/shared/assets/images/volume-off.svg';
import type { VideoPlayerProps } from './VideoPlayer.types';
import { useTranslation } from 'react-i18next';

export default function VideoPlayer({ videoSrc, className, autoPlay = false }: VideoPlayerProps) {
  const { t } = useTranslation('common');

  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(autoPlay);
  const [isHovered, setIsHovered] = useState(false);

  // Load the source whenever it changes; when autoPlay is on, start playing.
  // Keyed on videoSrc (not just mount) so switching personas in the widget -
  // where the player is not remounted - re-triggers autoplay, and so the async
  // blob: URL from useGetDownloadUrl kicks it off once it resolves.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.load();
    if (autoPlay && videoSrc) {
      Promise.resolve(video.play())
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false)); // blocked by the browser -> reflect stopped state
    }
  }, [videoSrc, autoPlay]);

  // Reset play button when video ends
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleVideoEnd = () => {
      setIsPlaying(false);
      // Reset video to start frame
      video.currentTime = 0;
    };

    video.addEventListener('ended', handleVideoEnd);

    return () => {
      video.removeEventListener('ended', handleVideoEnd);
    };
  }, []);

  const handlePlayPause = (e?: React.MouseEvent) => {
    // Only handle clicks directly on the video element, not on child elements
    if (e && e.target !== e.currentTarget) {
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      void video.play();
      setIsPlaying(true);
    }
  };

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  return (
    <div
      className="rounded-2xl relative overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <video
        ref={videoRef}
        width="100%"
        preload="auto"
        muted={isMuted}
        playsInline
        style={{ display: 'block', width: '100%' }}
        className={`hover:cursor-pointer ${className || ''}`}
        onClick={(e) => handlePlayPause(e)}
      >
        <source src={videoSrc} type="video/mp4" />
        <track kind="captions" />
        {t('components.videoPlayer.yourBrowserDoesNotSupport')}
      </video>

      {isHovered && (
        <div className="flex gap-1 p-1 absolute bottom-2 left-2 w-fit">
          <button
            type="button"
            className="bg-black/40 hover:bg-black/50 rounded-xl px-2 py-2 cursor-pointer border-none"
            onClick={(e) => {
              e.stopPropagation();
              handlePlayPause();
            }}
            aria-label={
              isPlaying
                ? t('components.videoPlayer.pauseVideo')
                : t('components.videoPlayer.playVideo')
            }
          >
            <OptimizedImage
              src={isPlaying ? PauseIcon : PlayIcon}
              alt={isPlaying ? t('components.videoPlayer.pause') : t('components.videoPlayer.play')}
              width={20}
              height={20}
            />
          </button>

          <button
            type="button"
            className="bg-black/40 hover:bg-black/50 rounded-xl px-2 py-2 cursor-pointer border-none"
            onClick={(e) => {
              e.stopPropagation();
              handleMuteToggle();
            }}
            aria-label={
              isMuted
                ? t('components.videoPlayer.unmuteVideo')
                : t('components.videoPlayer.muteVideo')
            }
          >
            <OptimizedImage
              src={isMuted ? VolumeOffIcon : VolumeHighIcon}
              alt={t('components.videoPlayer.mute')}
              width={20}
              height={20}
            />
          </button>
        </div>
      )}
    </div>
  );
}
